import { transportLayersEndpoints } from '@ooh/contracts';
import type { EndpointDef, callEndpoint } from '@ooh/contracts';

// ─── Shared auth interface (mirrors POI query pattern) ────────────────────────

export interface WithCallWithAuth {
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

// ─── Bbox params ──────────────────────────────────────────────────────────────

export interface TransportLineBboxParams {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
  /** Comma-separated scope string, e.g. 'public' | 'roads' | 'public,roads' */
  scopes?: string;
}

// ─── Query factories ──────────────────────────────────────────────────────────

export const transportLayerQueries = {
  /**
   * Query factory for bbox-scoped transport line features.
   *
   * The caller is responsible for:
   * - Passing a debounced bbox (to avoid over-fetching on pan/zoom)
   * - Setting `enabled: false` when no scopes are active or bbox is null
   */
  listByBbox: (auth: WithCallWithAuth, params: TransportLineBboxParams) => ({
    queryKey: [
      'transport-lines',
      'list',
      params.minLon,
      params.minLat,
      params.maxLon,
      params.maxLat,
      params.scopes ?? null,
    ] as const,
    queryFn: () =>
      auth.callWithAuth(transportLayersEndpoints.listByBbox, {
        minLon: params.minLon,
        minLat: params.minLat,
        maxLon: params.maxLon,
        maxLat: params.maxLat,
        scopes: params.scopes,
      }),
  }),

  /**
   * Query factory for nearby transport context for a selected media unit.
   *
   * The caller is responsible for setting `enabled: false` when:
   * - No transport scope is active, OR
   * - No media unit is selected (mediaUnitId is null/undefined)
   */
  nearbyTransport: (auth: WithCallWithAuth, mediaUnitId: number) => ({
    queryKey: ['transport-lines', 'nearby', mediaUnitId] as const,
    queryFn: () => auth.callWithAuth(transportLayersEndpoints.nearbyTransport, { id: mediaUnitId }),
  }),

  /**
   * Query factory for transport layer import status (admin-only).
   */
  importStatus: (auth: WithCallWithAuth) => ({
    queryKey: ['transport-lines', 'import', 'status'] as const,
    queryFn: () => auth.callWithAuth(transportLayersEndpoints.importStatus, {}),
  }),
};
